import {
  ESButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  ESSelect,
  useESSnackbar,
} from '@energy-stacks/core/ui';
import { Dialog } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ChargingStationDetailsModel,
  TriggerMessageRequestOption,
  useTriggerMessageMutation,
} from '@energy-stacks/broker/feature-charging-stations-data';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TriggerChargingStationMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  identityKey: ChargingStationDetailsModel['identityKey'];
}

export const TriggerChargingStationMessageDialog: React.FC<
  TriggerChargingStationMessageDialogProps
> = ({ identityKey, isOpen, onClose }) => {
  const { t } = useTranslation('chargingStations');
  const { showSnackbar } = useESSnackbar();
  const allowedMessages: TriggerMessageRequestOption[] = [
    'BootNotification',
    'DiagnosticsStatusNotification',
    'FirmwareStatusNotification',
    'Heartbeat',
  ]; // on the whole charging station level, only these messages can be triggered
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
    getValues,
  } = useForm<{ requestedMessage: TriggerMessageRequestOption }>({
    resolver: yupResolver(
      yup.object().shape({
        requestedMessage: yup.string().required().oneOf(allowedMessages),
      })
    ),
    mode: 'onChange',
    defaultValues: {
      requestedMessage: undefined,
    },
  });
  const [triggerMessage, { isLoading }] = useTriggerMessageMutation();
  const requestRef = useRef<ReturnType<typeof triggerMessage>>();

  const onSubmit = useCallback(() => {
    const triggerChargingStationMessage = triggerMessage({
      identityKey: identityKey,
      requestedMessage: getValues().requestedMessage,
      // to trigger message on the whole charging station level, we do not provide a connector id in the arguments
    });

    requestRef.current = triggerChargingStationMessage;

    triggerChargingStationMessage
      .unwrap()
      .then((result) => {
        if (result.status === 'Rejected') {
          showSnackbar('error', t('triggerMessageRejectedErrorMessage'));
          return;
        }

        if (result.status === 'NotImplemented') {
          showSnackbar('error', t('triggerMessageNotImplementedMessage'));
          return;
        }

        if (result.status === 'Accepted') {
          showSnackbar('success', t('triggerMessageSuccessMessage'));
        }

        onClose();
      })
      .catch((error) => {
        if (error.data?.errorCode === 'RPC_TIMEOUT') {
          showSnackbar('error', t('rpcTimeoutError'));
          return;
        }
        if (error.data?.errorCode === 'RPC_ERROR') {
          showSnackbar('error', t('rpcError'));
          return;
        }

        if (error.name === 'AbortError') {
          return;
        }

        showSnackbar('error');
      });
  }, [identityKey, triggerMessage, getValues, onClose, t, showSnackbar]);

  const handleCancel = useCallback(() => {
    requestRef.current?.abort();
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <ESDialogTitle data-testid="triggerMessagetitle">
        {t('remoteTriggerMessage')}
      </ESDialogTitle>

      <ESDialogContent>
        <Controller
          control={control}
          name="requestedMessage"
          render={({ field: { value, onChange, onBlur } }) => (
            <ESSelect
              testId="triggerMessage"
              label={t('messageType')}
              options={allowedMessages.map((option) => ({
                value: option,
                label: t(`triggerMessageRequestOption.${option}`),
              }))}
              onChange={onChange}
              onBlur={onBlur}
              value={value || ''}
              required
              disabled={isLoading}
              sx={{ mt: 2 }}
            />
          )}
        />
      </ESDialogContent>

      <ESDialogActions>
        <ESButton
          data-testid="triggerMessageCancelButton"
          variant="text"
          color="error"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </ESButton>

        <ESButton
          data-testid="triggerMessageTriggerButton"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {t('remoteTriggerMessage')}
        </ESButton>
      </ESDialogActions>
    </Dialog>
  );
};
